import { useEffect, useState } from 'react';
import { useVisualizerStore } from './stores/useVisualizerStore';
import { UploadScreen } from './screens/UploadScreen';
import { ConfigureScreen } from './screens/ConfigureScreen';
import { ProcessingScreen } from './screens/ProcessingScreen';
import { PreviewScreen } from './screens/PreviewScreen';
import { ErrorScreen } from './screens/ErrorScreen';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { Toasts } from './components/Toasts';
import { PwaUpdatePrompt } from './components/PwaUpdatePrompt';
import { ErrorBoundary } from './components/ErrorBoundary';
import { FeedbackForm } from './components/FeedbackForm';
import { UnsupportedBrowser } from './components/UnsupportedBrowser';
import { initPostMessage, cleanupPostMessage, requestResize } from './embed/postMessage';
import { trackEvent } from './api/analytics';
import { detectDeviceCapabilities } from './utils/deviceTier';
import { isEmbedded } from './utils/config';

const IS_EMBED = isEmbedded();

function App() {
  const appState = useVisualizerStore((s) => s.appState);

  useEffect(() => {
    initPostMessage();
    trackEvent('page_view');

    return () => {
      cleanupPostMessage();
    };
  }, []);

  // ── Embed mode: let the document flow to its content height ───────────────
  // The standalone app pins html/body/#root to height:100%;overflow:hidden so
  // it behaves like a fixed viewport. Inside a partner iframe we instead want
  // the document to grow to its content so the host can size the iframe to fit
  // (no internal scrollbar). We override the pinned styles imperatively.
  useEffect(() => {
    if (!IS_EMBED) return;
    const targets = [
      document.documentElement,
      document.body,
      document.getElementById('root'),
    ].filter((el): el is HTMLElement => Boolean(el));

    const prev = targets.map((el) => ({ el, height: el.style.height, overflow: el.style.overflow, minHeight: el.style.minHeight }));
    targets.forEach((el) => {
      el.style.height = 'auto';
      el.style.minHeight = '100%';
      el.style.overflow = 'visible';
    });

    return () => {
      prev.forEach(({ el, height, overflow, minHeight }) => {
        el.style.height = height;
        el.style.overflow = overflow;
        el.style.minHeight = minHeight;
      });
    };
  }, []);

  // ── Embed mode: report content height to the host iframe ──────────────────
  useEffect(() => {
    if (!IS_EMBED) return;

    let frame = 0;
    let lastSent = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const height = document.documentElement.scrollHeight;
        // Avoid chatty sub-pixel updates that can cause height jitter.
        if (Math.abs(height - lastSent) < 2) return;
        lastSent = height;
        requestResize(height);
      });
    };

    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    window.addEventListener('load', measure);
    measure();

    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      window.removeEventListener('load', measure);
    };
  }, []);

  // Check browser support on mount
  const [isSupported, setIsSupported] = useState(true);
  useEffect(() => {
    const caps = detectDeviceCapabilities();
    // Require at least Canvas 2D support
    if (!caps.canvas2dSupported) {
      setIsSupported(false);
    }
  }, []);

  if (!isSupported) {
    return (
      <div className="flex h-full flex-col bg-white">
        <Header />
        <main className="flex-1 overflow-hidden">
          <UnsupportedBrowser />
        </main>
        <Footer />
      </div>
    );
  }

  const screenKey = (() => {
    switch (appState) {
      case 'IDLE':
      case 'UPLOADING':
      case 'VALIDATING':
        return 'upload';
      case 'CONFIGURING':
        return 'configure';
      case 'MODEL_LOADING':
      case 'DETECTING':
      case 'RENDERING':
      case 'SUBMITTING_JOB':
      case 'PROCESSING_ON_SERVER':
        return 'processing';
      case 'PREVIEW':
      case 'SAVING':
        return 'preview';
      case 'ERROR':
      case 'MANUAL_PROMPT':
        return 'error';
      default:
        return 'upload';
    }
  })();

  const renderScreen = () => {
    switch (appState) {
      case 'IDLE':
      case 'UPLOADING':
      case 'VALIDATING':
        return <UploadScreen />;
      case 'CONFIGURING':
        return <ConfigureScreen />;
      case 'MODEL_LOADING':
      case 'DETECTING':
      case 'RENDERING':
      case 'SUBMITTING_JOB':
      case 'PROCESSING_ON_SERVER':
        return <ProcessingScreen />;
      case 'PREVIEW':
      case 'SAVING':
        return <PreviewScreen />;
      case 'ERROR':
      case 'MANUAL_PROMPT':
        return <ErrorScreen />;
      default:
        return <UploadScreen />;
    }
  };

  return (
    <ErrorBoundary>
      <div className={`flex flex-col bg-white ${IS_EMBED ? 'min-h-full' : 'h-full'}`}>
        <Header />
        <main className={IS_EMBED ? 'w-full' : 'flex-1 overflow-hidden'}>
          <div key={screenKey} className={`animate-screen-enter ${IS_EMBED ? '' : 'h-full'}`}>
            {renderScreen()}
          </div>
        </main>
        <Footer />
        <Toasts />
        <PwaUpdatePrompt />
        <FeedbackForm />
      </div>
    </ErrorBoundary>
  );
}

export default App;
